// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// components
import Iconify from 'src/components/iconify';
// assets
import { MaintenanceIllustration } from 'src/assets/illustrations';

// ----------------------------------------------------------------------

const SOCIAL_LINKS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61588741757316',
    icon: 'eva:facebook-fill',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/safi_swap/',
    icon: 'ant-design:instagram-filled',
  },
];

export default function MaintenanceView() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 3, md: 8 },
        py: { xs: 8, md: 12 },
        textAlign: 'center',
        background: `radial-gradient(circle at 50% 0%, ${alpha(
          theme.palette.primary.main,
          0.12
        )} 0%, transparent 60%)`,
      }}
    >
      {/* Ambient blurred orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: -120,
          left: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          filter: 'blur(80px)',
          opacity: 0.5,
          bgcolor: alpha(theme.palette.primary.main, 0.24),
          animation: 'float 12s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -140,
          right: -100,
          width: 360,
          height: 360,
          borderRadius: '50%',
          filter: 'blur(90px)',
          opacity: 0.4,
          bgcolor: alpha(theme.palette.info.main, 0.24),
          animation: 'float 14s ease-in-out infinite reverse',
        }}
      />

      <Stack sx={{ position: 'relative', alignItems: 'center' }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            mb: 3,
            borderRadius: 5,
            typography: 'caption',
            fontWeight: 'fontWeightSemiBold',
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'primary.main',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.24)}`,
          }}
        >
          Scheduled maintenance
        </Box>

        <Typography variant="h2" sx={{ mb: 2, maxWidth: 480 }}>
          We&apos;ll be back shortly
        </Typography>

        <Typography sx={{ color: 'text.secondary', maxWidth: 400 }}>
          We&apos;re making a few improvements behind the scenes. Thanks for your patience — check
          back in a moment.
        </Typography>

        <MaintenanceIllustration sx={{ my: { xs: 6, md: 8 }, height: 240 }} />
      </Stack>

      {/* Social footer */}
      <Stack
        spacing={1.5}
        sx={{
          position: 'absolute',
          bottom: { xs: 24, md: 32 },
          left: 0,
          right: 0,
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'center' }}>
          {SOCIAL_LINKS.map((social) => {
            return (
              <Link
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  color: 'text.secondary',
                  transition: (t) =>
                    t.transitions.create(['color', 'background-color', 'transform']),
                  bgcolor: (t) => alpha(t.palette.grey[500], 0.08),
                  '&:hover': {
                    color: 'primary.main',
                    transform: 'translateY(-2px)',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  },
                }}
              >
                <Iconify icon={social.icon} width={22} />
              </Link>
            );
          })}
        </Stack>

        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          © {new Date().getFullYear()} SafiSwap
        </Typography>
      </Stack>

      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes float {
              0%, 100% { transform: translate(0, 0); }
              50% { transform: translate(20px, 30px); }
            }
            @media (prefers-reduced-motion: reduce) {
              * { animation: none !important; }
            }
          `,
        }}
      />
    </Box>
  );
}
